import type { TFunction } from 'i18next';
import { z } from 'zod';

import {
	DcrhcpInputSchema,
	HcpSchema
} from '@/core/graphql/generated/validation.zod';
import { emailValidator } from '@/utils/validators/zod/emailValidator';
import { phoneNumberValidator } from '@/utils/validators/zod/phoneNumberValidator';
import { websiteValidator } from '@/utils/validators/zod/websiteValidator';

export const ContactSectionSchema = () => {
	const raw = HcpSchema().pick({
		officePhoneCda_V: true,
		phone2_V: true,
		faxCda_V: true,
		websiteCda_V: true
	});

	// Take the email fields from DcrhcpInputSchema (target payload) instead of HcpSchema to validate them.
	const emailsRaw = DcrhcpInputSchema().pick({
		emailCda_V: true,
		email2_V: true,
		email3_V: true,
		email4_V: true,
		email5_V: true
	});

	return z.object({
		officePhoneCda_V: phoneNumberValidator(raw.shape.officePhoneCda_V),
		phone2_V: phoneNumberValidator(raw.shape.phone2_V),

		faxCda_V: phoneNumberValidator(raw.shape.faxCda_V),

		emailCda_V: emailValidator(emailsRaw.shape.emailCda_V),
		email2_V: emailValidator(emailsRaw.shape.email2_V),
		email3_V: emailValidator(emailsRaw.shape.email3_V),
		email4_V: emailValidator(emailsRaw.shape.email4_V),
		email5_V: emailValidator(emailsRaw.shape.email5_V),

		websiteCda_V: websiteValidator(raw.shape.websiteCda_V)
	});
};

export type ContactSectionValuesIn = z.input<
	ReturnType<typeof ContactSectionSchema>
>;
export type ContactSectionValuesOut = z.output<
	ReturnType<typeof ContactSectionSchema>
>;

export const EMAIL_FIELD_KEYS = [
	'emailCda_V',
	'email2_V',
	'email3_V',
	'email4_V',
	'email5_V'
] as const;

export const isUnchangedEmailField = (
	key: string,
	currentVal: unknown,
	defaultVal: unknown
): boolean =>
	(EMAIL_FIELD_KEYS as readonly string[]).includes(key) &&
	(currentVal ?? '') === (defaultVal ?? '');

type EmailSequenceData = Record<(typeof EMAIL_FIELD_KEYS)[number], string>;

export const validateEmailSequence =
	(t: TFunction, allowMultipleEmails: boolean) =>
	(data: EmailSequenceData, ctx: z.RefinementCtx) => {
		if (!allowMultipleEmails) return;

		// It checks if there was any empty email field before the current one
		// e.g. email3 is filled but email2 is empty, which is invalid.
		let seenEmpty = false;
		EMAIL_FIELD_KEYS.forEach(key => {
			if (!data[key]) {
				seenEmpty = true;
				return;
			}

			if (seenEmpty) {
				ctx.addIssue({
					code: 'custom',
					path: [key],
					message: t
						? t('Fill in the previous email field first')
						: 'Fill in the previous email field first'
				});
			}
		});
	};

const emailRowShape = Object.fromEntries(
	EMAIL_FIELD_KEYS.map(key => [key, z.string()])
) as Record<(typeof EMAIL_FIELD_KEYS)[number], z.ZodString>;

export const EmailSequenceSchema = (
	t: TFunction,
	allowMultipleEmails: boolean
) =>
	z
		.object(emailRowShape)
		.superRefine(validateEmailSequence(t, allowMultipleEmails));
